/**
 * usePermissions(subMenuLink)
 *
 * Reads the current user's menu_access from Redux state (login response)
 * and returns permission flags for the given sub-menu route.
 *
 * Usage:
 *   const { canView, canCreate, canEdit, canDelete, importExport } =
 *     usePermissions("/cms/manage-customer");
 *
 * Defaults to all-true when no match is found (backward compat for screens
 * that don't have a corresponding sub_menu entry).
 */

import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth/authSlice";

const ALL_ALLOWED = {
  canView: true,
  canCreate: true,
  canEdit: true,
  canDelete: true,
  importExport: true,
};

const usePermissions = (subMenuLink) => {
  const authUser = useSelector(selectUser);
  const menuAccess = authUser?.menu_access;

  if (!Array.isArray(menuAccess)) return ALL_ALLOWED;

  for (const menu of menuAccess) {
    // Check top-level menu link
    if (menu.menu_link === subMenuLink) {
      return {
        canView:      !!menu.can_view,
        canCreate:    !!menu.can_create,
        canEdit:      !!menu.can_edit,
        canDelete:    !!menu.can_delete,
        importExport: !!menu.import_export,
      };
    }
    // Check sub-menus
    for (const sub of menu.sub_menus || []) {
      if (sub.sub_menu_link === subMenuLink) {
        return {
          canView:      !!sub.can_view,
          canCreate:    !!sub.can_create,
          canEdit:      !!sub.can_edit,
          canDelete:    !!sub.can_delete,
          importExport: !!sub.import_export,
        };
      }
    }
  }

  return ALL_ALLOWED;
};

export default usePermissions;
